import { useCallback, useEffect, useState, type ReactNode } from 'react';

import LucideSymbolIcon from '../../icons/LucideSymbolIcon';
import { fetchAuthenticatedFileBlob } from '../../../services/api';
import { CATEGORIE_LABELS, resolveIdentityCategorisation } from '../../../utils/categorieParticipant';

type IdentityReviewData = {
  learnerName: string;
  matricule?: string | null;
  email?: string | null;
  dateNaissance?: string | null;
  age?: number | null;
  categorie?: string | null;
  categorisationRaison?: string | null;
  profession?: string | null;
  pieceIdentiteId?: string | null;
  pieceIdentiteStatut?: string | null;
  pieceIdentiteType?: string | null;
  pieceIdentiteLabel?: string | null;
  /** Chemin API relatif pour charger la pièce d'identité déposée */
  pieceIdentiteApiPath?: string | null;
};

type Props = {
  open: boolean;
  onClose: () => void;
  data: IdentityReviewData | null;
};

function isImageType(contentType: string) {
  return contentType.startsWith('image/');
}

function isPdfType(contentType: string) {
  return contentType === 'application/pdf' || contentType.includes('pdf');
}

function formatDate(iso?: string | null) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}

function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

const STATUT_LABELS: Record<string, string> = {
  EN_ATTENTE: 'En attente',
  VALIDE: 'Validé',
  REJETE: 'Rejeté',
};

const STATUT_STYLES: Record<string, string> = {
  EN_ATTENTE: 'bg-amber-100 text-amber-900 ring-amber-200/80',
  VALIDE: 'bg-emerald-100 text-emerald-800 ring-emerald-200/80',
  REJETE: 'bg-red-100 text-red-800 ring-red-200/80',
};

const CATEGORIE_STYLES: Record<string, { card: string; badge: string; icon: string }> = {
  JEUNESSE_PRIORITAIRE: {
    card: 'from-emerald-50/90 via-white to-white border-emerald-200/60',
    badge: 'bg-emerald-600 text-white',
    icon: 'text-emerald-700 bg-emerald-100',
  },
  ADULTE_STANDARD: {
    card: 'from-primary/8 via-white to-white border-primary/20',
    badge: 'bg-primary text-white',
    icon: 'text-primary bg-primary/10',
  },
  FONCTIONNAIRE_EMPLOYE: {
    card: 'from-sky-50/90 via-white to-white border-sky-200/60',
    badge: 'bg-sky-700 text-white',
    icon: 'text-sky-700 bg-sky-100',
  },
};

function categorieTheme(categorie?: string | null) {
  if (categorie && CATEGORIE_STYLES[categorie]) return CATEGORIE_STYLES[categorie];
  return CATEGORIE_STYLES.ADULTE_STANDARD;
}

function DetailTile({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="rounded-xl border border-outline-variant/15 bg-white px-3.5 py-3 shadow-sm">
      <dt className="text-[10px] font-bold uppercase tracking-wide text-on-surface-variant">{label}</dt>
      <dd className="mt-1 text-sm font-semibold text-slate-900">{value}</dd>
    </div>
  );
}

export default function LearnerIdentityReviewModal({ open, onClose, data }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [contentType, setContentType] = useState('');

  const cleanup = useCallback(() => {
    setBlobUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setContentType('');
    setError(null);
  }, []);

  const handleClose = useCallback(() => {
    cleanup();
    onClose();
  }, [cleanup, onClose]);

  useEffect(() => {
    if (!open) {
      cleanup();
      return undefined;
    }

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, loading, handleClose, cleanup]);

  useEffect(() => {
    if (!open || !data?.pieceIdentiteApiPath) {
      cleanup();
      return undefined;
    }

    let cancelled = false;
    let createdUrl: string | null = null;
    const apiPath = data.pieceIdentiteApiPath;

    (async () => {
      setLoading(true);
      setError(null);
      setBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setContentType('');

      try {
        const result = await fetchAuthenticatedFileBlob(apiPath);
        if (cancelled) {
          URL.revokeObjectURL(result.blobUrl);
          return;
        }
        createdUrl = result.blobUrl;
        setBlobUrl(result.blobUrl);
        setContentType(result.contentType);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Impossible de charger la pièce d'identité.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [open, data?.pieceIdentiteApiPath, cleanup]);

  if (!open || !data) {
    return null;
  }

  const { age, categorie, categorisationRaison } = resolveIdentityCategorisation(data);
  const categorieLabel = categorie ? (CATEGORIE_LABELS[categorie] ?? categorie) : 'Non déterminée';
  const theme = categorieTheme(categorie);
  const statutLabel = data.pieceIdentiteStatut
    ? (STATUT_LABELS[data.pieceIdentiteStatut] ?? data.pieceIdentiteStatut)
    : null;
  const hasPieceIdentite = Boolean(data.pieceIdentiteApiPath);
  const statutStyle = data.pieceIdentiteStatut
    ? (STATUT_STYLES[data.pieceIdentiteStatut] ?? STATUT_STYLES.EN_ATTENTE)
    : STATUT_STYLES.EN_ATTENTE;

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 p-4"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget && !loading) handleClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="identity-review-title"
        className="flex max-h-[min(90vh,900px)] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-outline-variant/20 bg-surface-container-lowest shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="relative shrink-0 overflow-hidden border-b border-outline-variant/15 bg-gradient-to-br from-primary/10 via-white to-white px-5 py-5">
          <div className="pointer-events-none absolute -right-8 -top-10 h-36 w-36 rounded-full bg-primary/5" aria-hidden />
          <div className="relative flex items-start justify-between gap-4">
            <div className="flex min-w-0 items-start gap-4">
              <span
                className={`inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-sm font-black ${theme.icon}`}
                aria-hidden
              >
                {initials(data.learnerName)}
              </span>
              <div className="min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-primary">
                  Validation dossier
                </p>
                <h2 id="identity-review-title" className="mt-0.5 truncate text-xl font-black text-slate-900">
                  {data.learnerName}
                </h2>
                <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-on-surface-variant">
                  {data.matricule ? (
                    <span className="inline-flex items-center gap-1 font-mono text-xs">
                      <LucideSymbolIcon name="badge" tone="muted" size={14} />
                      {data.matricule}
                    </span>
                  ) : null}
                  {data.email ? <span>{data.email}</span> : null}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-outline-variant/20 bg-white/90 text-on-surface-variant shadow-sm transition hover:bg-white hover:text-slate-900"
              aria-label="Fermer"
            >
              <LucideSymbolIcon name="close" tone="inherit" size={20} />
            </button>
          </div>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="grid gap-0 lg:grid-cols-[minmax(0,22rem)_1fr]">
            <aside className="space-y-4 border-b border-outline-variant/15 bg-surface-container-low/60 p-5 lg:border-b-0 lg:border-r">
              <div
                className={`overflow-hidden rounded-2xl border bg-gradient-to-br p-4 shadow-sm ${theme.card}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wide text-on-surface-variant">
                      Catégorie tarifaire
                    </p>
                    <p className="mt-2 text-3xl font-black tabular-nums text-slate-900">
                      {age != null ? `${age}` : '—'}
                      {age != null ? (
                        <span className="ml-1 text-base font-bold text-on-surface-variant">ans</span>
                      ) : null}
                    </p>
                  </div>
                  <span
                    className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${theme.icon}`}
                    aria-hidden
                  >
                    <LucideSymbolIcon name="person" tone="inherit" size={20} />
                  </span>
                </div>
                <span
                  className={`mt-4 inline-flex max-w-full items-center rounded-full px-3 py-1.5 text-xs font-bold leading-snug ${theme.badge}`}
                >
                  {categorieLabel}
                </span>
              </div>

              <section>
                <h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-on-surface-variant">
                  Informations profil
                </h3>
                <dl className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-1">
                  <DetailTile label="Date de naissance" value={formatDate(data.dateNaissance)} />
                  {data.profession ? (
                    <DetailTile label="Profession déclarée" value={data.profession} />
                  ) : null}
                  {statutLabel ? (
                    <DetailTile
                      label="Statut pièce d'identité"
                      value={
                        <span
                          className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-bold ring-1 ring-inset ${statutStyle}`}
                        >
                          {statutLabel}
                        </span>
                      }
                    />
                  ) : null}
                </dl>
              </section>

              {categorisationRaison ? (
                <div className="academic-panel-stat-banner academic-panel-stat-banner--info !mb-0 !rounded-xl !text-left">
                  <span className="block text-[10px] font-bold uppercase tracking-wide opacity-80">
                    Justification
                  </span>
                  <span className="mt-1 block text-sm font-medium leading-relaxed">{categorisationRaison}</span>
                </div>
              ) : null}

              {!hasPieceIdentite ? (
                <p className="rounded-xl border border-amber-200/80 bg-amber-50 px-3.5 py-3 text-xs leading-relaxed text-amber-950">
                  Aucun justificatif d&apos;identité déposé — la catégorie affichée repose sur la date de naissance
                  renseignée au profil.
                </p>
              ) : null}
            </aside>

            <section className="flex min-h-[320px] flex-col bg-slate-50/80">
              <div className="flex shrink-0 items-center justify-between gap-3 border-b border-outline-variant/10 px-5 py-3.5">
                <div className="flex items-center gap-2">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <LucideSymbolIcon name="description" tone="inherit" size={18} />
                  </span>
                  <div>
                    <p className="text-sm font-bold text-slate-900">
                      {data.pieceIdentiteLabel ?? "Justificatif d'identité"}
                    </p>
                    <p className="text-xs text-on-surface-variant">
                      Document officiel pour confirmer la catégorie d&apos;âge
                    </p>
                  </div>
                </div>
                {blobUrl ? (
                  <a
                    href={blobUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-primary/20 bg-white px-3 py-1.5 text-xs font-bold text-primary shadow-sm transition hover:bg-primary/5"
                  >
                    <LucideSymbolIcon name="public" tone="inherit" size={14} />
                    Ouvrir
                  </a>
                ) : null}
              </div>

              <div className="flex flex-1 items-center justify-center overflow-auto p-5">
                {!hasPieceIdentite ? (
                  <div className="max-w-sm rounded-2xl border border-dashed border-outline-variant/30 bg-white px-8 py-10 text-center shadow-sm">
                    <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-surface-container-low text-on-surface-variant">
                      <LucideSymbolIcon name="badge" tone="muted" size={28} />
                    </span>
                    <p className="mt-4 text-sm font-semibold text-slate-900">Document absent</p>
                    <p className="mt-2 text-sm leading-relaxed text-on-surface-variant">
                      L&apos;apprenant n&apos;a pas encore déposé de pièce d&apos;identité dans son dossier.
                    </p>
                  </div>
                ) : loading ? (
                  <div className="flex flex-col items-center gap-3 text-on-surface-variant">
                    <span className="inline-flex h-10 w-10 animate-pulse rounded-full bg-primary/10" aria-hidden />
                    <p className="text-sm font-medium">Chargement du document…</p>
                  </div>
                ) : error ? (
                  <div className="max-w-md rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-center">
                    <p className="text-sm font-semibold text-red-900">Impossible d&apos;afficher le document</p>
                    <p className="mt-1 text-sm text-red-800">{error}</p>
                  </div>
                ) : blobUrl && isImageType(contentType) ? (
                  <div className="w-full max-w-2xl rounded-2xl border border-outline-variant/15 bg-white p-3 shadow-md">
                    <img
                      src={blobUrl}
                      alt="Pièce d'identité"
                      className="mx-auto max-h-[min(58vh,560px)] w-full rounded-xl object-contain"
                    />
                  </div>
                ) : blobUrl && isPdfType(contentType) ? (
                  <div className="h-[min(58vh,560px)] w-full max-w-2xl overflow-hidden rounded-2xl border border-outline-variant/15 bg-white shadow-md">
                    <iframe title="Pièce d'identité" src={blobUrl} className="h-full w-full" />
                  </div>
                ) : blobUrl ? (
                  <div className="max-w-md rounded-2xl border border-outline-variant/15 bg-white p-8 text-center shadow-sm">
                    <LucideSymbolIcon name="description" tone="muted" size={40} className="mx-auto" />
                    <p className="mt-3 text-sm text-on-surface-variant">
                      Aperçu non disponible pour ce type de fichier.
                    </p>
                    <a
                      href={blobUrl}
                      download
                      className="mt-4 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-bold text-white"
                    >
                      <LucideSymbolIcon name="download" tone="inherit" size={18} className="text-white" />
                      Télécharger
                    </a>
                  </div>
                ) : null}
              </div>
            </section>
          </div>
        </div>

        <footer className="flex shrink-0 items-center justify-end gap-2 border-t border-outline-variant/15 bg-white px-5 py-3.5">
          <button
            type="button"
            onClick={handleClose}
            className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:opacity-95"
          >
            Fermer
          </button>
        </footer>
      </div>
    </div>
  );
}
